import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { SearchableSelect } from '../primitives/SearchableSelect.js';
import { waLink } from '../../lib/wa.js';

interface Project { id: string; code: string; name: string; status: string; documents?: Array<{ id: string; name: string; fileUrl: string | null }>; }
interface Worker { id: string; name: string; role: string; }
interface Contact { id: string; name: string; role: string | null; customer?: string | null; email: string | null; phone: string | null; }
interface Supplier { id: string; name: string; contacts: Array<{ id: string; name: string; role: string | null; email: string | null; phone: string | null }>; }
interface Deployment { workerId: string; projectId: string; status: string; }
type RecipientType = 'worker' | 'contact' | 'supplier';
interface Ref { type: RecipientType; id: string }
interface ShareResult { id: string; name: string; recipients: Array<{ workerId: string | null; workerName: string; workerPhone: string | null; workerEmail: string | null; notifiedAt: string | null; signToken?: string | null }>; }

interface Props { onClose: () => void; }

export function ShareDocModal({ onClose }: Props) {
  const qc = useQueryClient();
  const [source, setSource] = useState<'project' | 'upload'>('project');
  const [projectId, setProjectId] = useState('');
  const [docId, setDocId] = useState('');
  const [upload, setUpload] = useState<{ url: string; name: string; mime: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [tab, setTab] = useState<RecipientType>('worker');
  const [search, setSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [selected, setSelected] = useState<Ref[]>([]);
  const [message, setMessage] = useState('');
  const [requireSig, setRequireSig] = useState(true);
  const [notifyWA, setNotifyWA] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySMS, setNotifySMS] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });
  const { data: workers = [] } = useQuery({ queryKey: ['workers'], queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data) });
  const { data: contacts = [] } = useQuery({ queryKey: ['contacts'], queryFn: () => api.get<Contact[]>('/contacts').then((r) => r.data) });
  const { data: suppliers = [] } = useQuery({ queryKey: ['suppliers'], queryFn: () => api.get<Supplier[]>('/suppliers').then((r) => r.data) });
  const { data: deployments = [] } = useQuery({ queryKey: ['deployments'], queryFn: () => api.get<Deployment[]>('/deployments').then((r) => r.data) });

  const project = projects.find((p) => p.id === projectId);
  const docs = project?.documents ?? [];
  const supplierContacts = useMemo(() => suppliers.flatMap((s) => s.contacts.map((c) => ({ ...c, supplierName: s.name }))), [suppliers]);
  const onSite = useMemo(() => new Set(deployments.filter((d) => d.status === 'active' && (!siteFilter || d.projectId === siteFilter)).map((d) => d.workerId)), [deployments, siteFilter]);

  const q = search.toLowerCase().trim();
  const rows: Array<{ id: string; label: string; sub: string; type: RecipientType }> = useMemo(() => {
    if (tab === 'worker') return workers.filter((w) => !siteFilter || onSite.has(w.id)).map((w) => ({ id: w.id, label: w.name, sub: w.role, type: 'worker' as const }));
    if (tab === 'contact') return contacts.map((c) => ({ id: c.id, label: c.name, sub: [c.role, c.customer].filter(Boolean).join(' · ') || (c.email ?? ''), type: 'contact' as const }));
    return supplierContacts.map((c) => ({ id: c.id, label: c.name, sub: [c.role, c.supplierName].filter(Boolean).join(' · '), type: 'supplier' as const }));
  }, [tab, workers, contacts, supplierContacts, siteFilter, onSite]);
  const visible = rows.filter((r) => !q || r.label.toLowerCase().includes(q) || r.sub.toLowerCase().includes(q));

  const isSelected = (r: Ref) => selected.some((s) => s.type === r.type && s.id === r.id);
  const toggle = (r: Ref) => setSelected((p) => isSelected(r) ? p.filter((s) => !(s.type === r.type && s.id === r.id)) : [...p, r]);
  const selectVisible = () => setSelected((p) => [...p, ...visible.filter((r) => !p.some((s) => s.type === r.type && s.id === r.id)).map((r) => ({ type: r.type, id: r.id }))]);
  const clearTab = () => setSelected((p) => p.filter((s) => s.type !== tab));
  const countOf = (t: RecipientType) => selected.filter((s) => s.type === t).length;

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post<{ url: string; originalName: string; mime: string }>('/uploads/file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUpload({ url: res.data.url, name: res.data.originalName || file.name, mime: res.data.mime || file.type });
    } catch {
      toast.error('Upload failed', 'Try a smaller file or a different format.');
    } finally {
      setUploading(false);
    }
  };

  const ready = source === 'project' ? !!projectId && !!docId : !!upload;

  const share = useMutation({
    mutationFn: async () => api.post<ShareResult>('/shared-docs', {
      ...(source === 'project' ? { projectId, documentId: docId } : { name: upload!.name, url: upload!.url, mimeType: upload!.mime }),
      recipients: selected,
      message: message.trim() || null,
      requireSignature: requireSig,
      channels: [notifyWA && 'whatsapp', notifyEmail && 'email', notifySMS && 'sms'].filter(Boolean),
    }).then((r) => r.data),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['shared-docs'] });
      const text = (name: string, token: string | null | undefined) => `Hi ${name.split(' ')[0]}, CLS has shared a document for you to read and sign: ${doc.name}.${message.trim() ? ` "${message.trim()}"` : ''} ${token ? `Open your personal signing link (valid 30 days): ${window.location.origin}/sign/${token}` : `Open ${window.location.origin}/docsign with your CLS login.`}`;
      doc.recipients.forEach((r, i) => {
        const phone = r.workerPhone;
        if (!phone) return;
        if (notifyWA) setTimeout(() => window.open(waLink(phone, text(r.workerName, r.signToken)), '_blank'), i * 600);
        if (notifySMS) setTimeout(() => window.open(`sms:${phone.replace(/\s+/g, '')}?body=${encodeURIComponent(text(r.workerName, r.signToken))}`, '_blank'), i * 600 + 300);
      });
      const emailed = doc.recipients.filter((r) => r.notifiedAt).length;
      const noEmail = doc.recipients.filter((r) => !r.workerEmail).length;
      toast.success('Document shared', `${doc.recipients.length} recipient${doc.recipients.length === 1 ? '' : 's'}${notifyEmail ? ` · ${emailed} emailed${noEmail ? ` · ${noEmail} without an email address` : ''}` : ''}`);
      onClose();
    },
    onError: () => toast.error('Share failed'),
  });

  const tabBtn = (t: RecipientType, label: string) => (
    <button type="button" className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-ghost'}`} onClick={() => { setTab(t); setSearch(''); }} id={`sd-tab-${t}`}>
      {label}{countOf(t) ? ` (${countOf(t)})` : ''}
    </button>
  );

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 660 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">✍ Share Document for Signing</div>
        <div className="modal-sub">Pick a project document or upload a file, then choose who has to sign it</div>

        <div className="form-row">
          <label className="form-label">Document</label>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <button type="button" className={`btn btn-sm ${source === 'project' ? 'btn-primary' : 'btn-ghost'}`} id="sd-source-project" onClick={() => setSource('project')}>From a project</button>
            <button type="button" className={`btn btn-sm ${source === 'upload' ? 'btn-primary' : 'btn-ghost'}`} id="sd-source-upload" onClick={() => setSource('upload')}>Upload a file</button>
          </div>
          {source === 'project' ? (
            <div className="fg2">
              <SearchableSelect id="sd-project" value={projectId} onChange={(v) => { setProjectId(v); setDocId(''); }} placeholder="— Select project —" options={projects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))} />
              <SearchableSelect id="sd-doc" value={docId} onChange={setDocId} placeholder={projectId ? '— Select document —' : '— Pick a project first —'} disabled={!projectId} options={docs.map((d) => ({ value: d.id, label: d.name }))} />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--card)', border: `1px solid ${upload ? 'var(--border)' : 'var(--danger)'}`, borderRadius: 8, padding: 10 }}>
              <input ref={fileRef} type="file" id="sd-file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" style={{ display: 'none' }} onChange={(e) => pickFile(e.target.files?.[0])} />
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? 'Uploading…' : '📎 Choose file'}</button>
              <span style={{ fontSize: 11, color: upload ? 'var(--text)' : 'var(--muted)' }} id="sd-file-name">{upload ? upload.name : 'PDF, image or Word document'}</span>
            </div>
          )}
        </div>

        <div className="form-row">
          <label className="form-label">Recipients ({selected.length} selected)</label>
          <div style={{ background: 'var(--card)', border: `1px solid ${selected.length ? 'var(--border)' : 'var(--danger)'}`, borderRadius: 8, padding: 10 }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {tabBtn('worker', 'Workers')}{tabBtn('contact', 'Client contacts')}{tabBtn('supplier', 'Suppliers')}
              {tab === 'worker' ? (
                <select className="form-input" id="sd-site" value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} style={{ marginLeft: 'auto', width: 190, fontSize: 11 }}>
                  <option value="">All workers (CLS pool)</option>
                  {projects.filter((p) => p.status === 'live').map((p) => <option key={p.id} value={p.id}>On site: {p.code}</option>)}
                </select>
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 7, marginBottom: 8, alignItems: 'center' }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '5px 9px', gap: 6 }}>
                <span style={{ color: 'var(--faint)', fontSize: 13 }}>⌕</span>
                <input type="text" id="sd-worker-search" placeholder="Search by name, role or company…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 12, width: '100%' }} />
              </div>
              <button type="button" className="btn btn-ghost btn-xs" id="sd-select-all" onClick={selectVisible}>Select all shown</button>
              <button type="button" className="btn btn-ghost btn-xs" onClick={clearTab}>None</button>
            </div>
            <div id="sd-workers-checks" style={{ maxHeight: 200, overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              {visible.length === 0 ? <div style={{ fontSize: 11, color: 'var(--muted)', padding: 6 }}>Nobody matches</div> : visible.map((r) => (
                <label key={`${r.type}-${r.id}`} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, cursor: 'pointer', padding: 4, borderRadius: 4, background: isSelected(r) ? 'rgba(232,164,39,.15)' : 'var(--surface)' }}>
                  <input type="checkbox" checked={isSelected(r)} onChange={() => toggle(r)} style={{ accentColor: 'var(--accent)' }} />
                  <span style={{ flex: 1 }}>{r.label}</span>
                  <span style={{ color: 'var(--muted)', fontSize: 9 }}>{r.sub}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="form-row">
          <label className="form-label">Message (optional)</label>
          <textarea className="form-textarea" id="sd-message" placeholder="Please read and sign this document before your next shift…" value={message} onChange={(e) => setMessage(e.target.value)} />
        </div>
        <div className="form-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" id="sd-require-sig" checked={requireSig} onChange={(e) => setRequireSig(e.target.checked)} style={{ accentColor: 'var(--accent)', width: 16, height: 16 }} />
          <label htmlFor="sd-require-sig" style={{ fontSize: 12, cursor: 'pointer' }}>Require signature (not just acknowledgement)</label>
        </div>
        <div className="form-row">
          <label className="form-label">Notify Via</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, padding: '5px 10px', borderRadius: 7, border: '1px solid var(--border)', flex: 1, justifyContent: 'center', minWidth: 100 }}>
              <input type="checkbox" id="sd-notify-email" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} style={{ accentColor: 'var(--info)', width: 14, height: 14 }} />
              <span style={{ color: 'var(--info)', fontWeight: 600 }}>✉ Email (personal signing link)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, padding: '5px 10px', borderRadius: 7, border: '1px solid var(--border)', flex: 1, justifyContent: 'center', minWidth: 100 }}>
              <input type="checkbox" id="sd-notify-wa" checked={notifyWA} onChange={(e) => setNotifyWA(e.target.checked)} style={{ accentColor: '#25D366', width: 14, height: 14 }} />
              <span style={{ color: '#25D366', fontWeight: 600 }}>WhatsApp</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, padding: '5px 10px', borderRadius: 7, border: '1px solid var(--border)', flex: 1, justifyContent: 'center', minWidth: 100 }}>
              <input type="checkbox" id="sd-notify-sms" checked={notifySMS} onChange={(e) => setNotifySMS(e.target.checked)} style={{ accentColor: 'var(--accent)', width: 14, height: 14 }} />
              <span style={{ color: 'var(--accent)', fontWeight: 600 }}>📱 SMS</span>
            </label>
          </div>
          <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 5 }}>Email is sent by the CRM with a personal link that works without a login (valid 30 days) · WhatsApp and SMS open a pre-filled message per recipient · workers with a login also get an in-app notification</div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" id="sd-share-btn" onClick={() => share.mutate()} disabled={share.isPending || !ready || selected.length === 0}>
            {share.isPending ? 'Sharing…' : '✍ Share & Notify'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
